import { useCallback } from 'react';
import { api } from '@/services/api/config';
import { ApiResponse } from '@/types';

// Helper para logs solo en desarrollo
const isDev =
  import.meta.env.MODE === 'development' ||
  import.meta.env.MODE === 'staging' ||
  window.location.hostname === 'localhost';
const devLog = (...args: unknown[]) => {
  if (isDev) {
    console.log(...args);
  }
};

interface RefreshTokenResponse {
  token: string;
  refreshToken?: string;
}

/**
 * Hook simple para refresh de token
 * Solo se usa cuando TÚ quieras (navegaciones importantes, etc.)
 * NO automático - control total
 */
export const useTokenRefresh = () => {
  const refreshToken = useCallback(async (): Promise<boolean> => {
    const refreshTokenValue = localStorage.getItem('refreshToken');

    if (!refreshTokenValue) {
      devLog('🚪 No refresh token available');
      return false;
    }

    try {
      devLog('🔄 Attempting token refresh...');

      const response = await api.post<ApiResponse<RefreshTokenResponse>>('/auth/refresh-token', {
        token: refreshTokenValue,
      });

      const responseData = response.data as ApiResponse<RefreshTokenResponse>;

      if (responseData.success) {
        // Update tokens
        localStorage.setItem('token', responseData.data.token);
        if (responseData.data.refreshToken) {
          localStorage.setItem('refreshToken', responseData.data.refreshToken);
        }

        devLog('✅ Token refreshed successfully');
        return true;
      } else {
        devLog('❌ Token refresh failed:', responseData.error);
        return false;
      }
    } catch (error) {
      devLog('❌ Token refresh error:', error);
      return false;
    }
  }, []);

  const refreshTokenIfNeeded = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem('token');

    if (!token) {
      devLog('🚪 No token available');
      return false;
    }

    try {
      // Check if token is close to expiring (within 5 minutes)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        devLog('🚪 Invalid token format');
        return false;
      }

      const decoded = JSON.parse(window.atob(tokenParts[1]));
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = decoded.exp - now;

      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 300) {
        // 5 minutes = 300 seconds
        devLog('⏰ Token expires soon, refreshing...');
        return await refreshToken();
      }

      devLog('✅ Token still valid, no refresh needed');
      return true;
    } catch (error) {
      devLog('❌ Error checking token expiry:', error);
      return false;
    }
  }, [refreshToken]);

  return {
    refreshToken, // Force refresh
    refreshTokenIfNeeded, // Refresh only if needed (close to expiry)
  };
};
